import { Controller } from "@hotwired/stimulus"

// The composer card (assistants/_outgoing_draft_card): an email or DM Jane
// drafted, edited in place and sent from the user's own mailbox / account.
//
// - Every edit autosaves (debounced PATCH, optimistic-locked by lockVersion),
//   so the server always holds what the card shows and Send sends exactly that.
// - Send starts the server's Undo window; the card counts it down and then
//   watches the draft until it reports sent or failed.
// - The chat poll re-renders the whole chat every ~1.2s while Jane is
//   replying. assistant_poll_controller keeps THIS card's node (data-live-card) (and the caret)
//   when the card has unsaved edits or the server copy is not newer, which is
//   why connect() may run more than once on the same element.
const SAVE_DELAY_MS = 600
const SEARCH_DELAY_MS = 160
const WATCH_INTERVAL_MS = 1500
const WATCH_LIMIT_MS = 45000
const EMAIL = /^[^@\s<>,;]+@[^@\s<>,;]+\.[^@\s<>,;]+$/

export default class extends Controller {
    static targets = [
        "toRow", "ccRow", "bccRow", "toChips", "ccChips", "bccChips", "ccToggle", "suggestions",
        "subject", "body", "signature", "signatureText", "signatureToggle", "signatureLabel",
        "send", "split", "caret", "menu", "copyButton", "note", "error", "countdown", "chipTemplate", "mailbox",
        "chatPick", "chatButton", "chatName", "chatMenu", "chatFilter", "chatList", "toolbar", "scheduleAt", "fileInput"
    ]

    static values = {
        id: Number,
        status: String,
        channel: String,
        lockVersion: Number,
        sendAt: String,
        updateUrl: String,
        deliverUrl: String,
        undoUrl: String,
        saveUrl: String,
        discardUrl: String,
        scheduleUrl: String,
        unscheduleUrl: String,
        chatsUrl: String,
        attachUrl: String,
        recipientsUrl: String,
        provider: String
    }

    connect() {
        if (this.statusValue === "sending" || this.statusValue === "delivering") this._startCountdown()
        this.element.querySelectorAll(".outgoing-chip-face img").forEach((img) => this._dropIfBroken(img))
        if (this._ready) return
        this._ready = true
        this._dirty = false
        this._suggestions = []
        this._active = -1
        this._onDocumentClick = (event) => {
            if (this.hasMenuTarget && !this.menuTarget.hidden && !this.splitTarget.contains(event.target)) this._closeMenu()
            if (this.hasChatMenuTarget && !this.chatMenuTarget.hidden && !this.chatPickTarget.contains(event.target)) this._closeChats()
        }
        document.addEventListener("click", this._onDocumentClick)
        // Remember where the caret was in the body, so an emoji or a link picked
        // from a popover goes back to that spot.
        this._onSelection = () => {
            const selection = window.getSelection()
            if (!this.hasBodyTarget || !selection || selection.rangeCount === 0) return
            const range = selection.getRangeAt(0)
            if (this.bodyTarget.contains(range.startContainer)) this._bodyRange = range.cloneRange()
        }
        document.addEventListener("selectionchange", this._onSelection)
        this.element.addEventListener("emoji-picker:picked", (event) => this.insertEmoji(event))
    }

    disconnect() {
        clearInterval(this._countdownTimer)
        clearTimeout(this._watchTimer)
        // A poll re-render detaches the node for a moment; anything typed must
        // already be on its way to the server rather than waiting on a timer.
        if (this._dirty) this._save({ keepalive: true })
        if (!this.element.isConnected) {
            document.removeEventListener("click", this._onDocumentClick)
            document.removeEventListener("selectionchange", this._onSelection)
        }
    }

    // --- DM formatting, emoji, conversation ---------------------------------

    // mousedown, not click: the body keeps its selection.
    format(event) {
        event.preventDefault()
        if (!this.editable) return
        const command = event.currentTarget.dataset.command
        this._restoreBodyRange()
        if (command === "createLink") {
            const url = window.prompt("Link to", "https://")
            if (!url || !/^(https?:\/\/|mailto:)/i.test(url.trim())) return
            if (window.getSelection().isCollapsed) document.execCommand("insertText", false, url.trim())
            else document.execCommand("createLink", false, url.trim())
        } else if (command === "blockquote" || command === "codeBlock") {
            const tag = command === "blockquote" ? "blockquote" : "pre"
            const inside = this._closest(tag)
            document.execCommand("formatBlock", false, inside ? "p" : tag)
        } else if (command === "code") {
            this._wrapInline("code")
        } else {
            document.execCommand(command, false, null)
        }
        this.changed()
    }

    insertEmoji(event) {
        const emoji = event.detail?.emoji
        if (!emoji || !this.editable || !this.hasBodyTarget) return
        this._restoreBodyRange()
        document.execCommand("insertText", false, emoji)
        this.changed()
    }

    toggleChats(event) {
        event.stopPropagation()
        if (!this.hasChatMenuTarget) return
        const opening = this.chatMenuTarget.hidden
        if (!opening) return this._closeChats()
        this.chatMenuTarget.hidden = false
        this.chatButtonTarget.setAttribute("aria-expanded", "true")
        this.chatFilterTarget.value = ""
        this.chatFilterTarget.focus()
        this._loadChats("")
    }

    filterChats() {
        clearTimeout(this._chatTimer)
        this._chatTimer = setTimeout(() => this._loadChats(this.chatFilterTarget.value), SEARCH_DELAY_MS)
    }

    chatKeydown(event) {
        const rows = Array.from(this.chatListTarget.querySelectorAll(".outgoing-chat-row"))
        if (event.key === "Escape") return this._closeChats()
        if (rows.length === 0) return
        const current = rows.findIndex((row) => row.classList.contains("is-active"))
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault()
            const next = event.key === "ArrowDown" ? Math.min(current + 1, rows.length - 1) : Math.max(current - 1, 0)
            rows.forEach((row, i) => row.classList.toggle("is-active", i === next))
        } else if (event.key === "Enter") {
            event.preventDefault()
            rows[Math.max(current, 0)].click()
        }
    }

    // --- attachments ----------------------------------------------------------

    pickFiles() {
        if (this.hasFileInputTarget) this.fileInputTarget.click()
    }

    upload() {
        const files = Array.from(this.fileInputTarget.files || [])
        this.fileInputTarget.value = ""
        this._attach(files)
    }

    dragOver(event) {
        if (!this.editable || !Array.from(event.dataTransfer?.types || []).includes("Files")) return
        event.preventDefault()
        this.element.classList.add("outgoing--dropping")
    }

    dragLeave(event) {
        if (!this.element.contains(event.relatedTarget)) this.element.classList.remove("outgoing--dropping")
    }

    drop(event) {
        const files = Array.from(event.dataTransfer?.files || [])
        this.element.classList.remove("outgoing--dropping")
        if (!this.editable || files.length === 0) return
        event.preventDefault()
        this._attach(files)
    }

    async detach(event) {
        const url = event.currentTarget.dataset.url
        await this._flush()
        try {
            const data = await this._request(url, "DELETE", {})
            this._swap(data.html)
        } catch (err) {
            this._error(err.message)
        }
    }

    // Saves first (so the re-rendered card can't lose an edit), then uploads and
    // shows the card as the server now has it.
    async _attach(files) {
        if (files.length === 0 || this._busy) return
        await this._flush()
        const form = new FormData()
        files.forEach((file) => form.append("files[]", file))
        this._note(files.length === 1 ? `Attaching ${files[0].name}…` : `Attaching ${files.length} files…`)
        try {
            const response = await fetch(this.attachUrlValue, {
                method: "POST",
                credentials: "same-origin",
                headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "" },
                body: form
            })
            const data = await response.json().catch(() => ({}))
            if (!response.ok || data.ok === false) throw new Error(data.error || "Couldn't attach that - try again.")
            this._swap(data.html)
        } catch (err) {
            this._note("")
            this._error(err.message)
        }
    }

    // --- Slack scheduling ---------------------------------------------------

    schedulePreset(event) {
        const at = new Date()
        at.setHours(9, 0, 0, 0)
        if (event.currentTarget.dataset.preset === "monday") {
            const ahead = (8 - at.getDay()) % 7 || 7
            at.setDate(at.getDate() + ahead)
        } else {
            at.setDate(at.getDate() + 1)
        }
        this._schedule(at)
    }

    scheduleCustom() {
        const value = this.hasScheduleAtTarget ? this.scheduleAtTarget.value : ""
        const at = value ? new Date(value) : null
        if (!at || Number.isNaN(at.getTime())) return this._error("Pick a date and time first.")
        this._schedule(at)
    }

    async _schedule(at) {
        this._closeMenu()
        await this._flush()
        this._transition(this.scheduleUrlValue, { payload: { post_at: at.toISOString() }, pending: "Scheduling…" })
    }

    unschedule() {
        this._transition(this.unscheduleUrlValue, { pending: "Cancelling…" })
    }

    // --- editing ------------------------------------------------------------

    changed() {
        this._markDirty()
        clearTimeout(this._saveTimer)
        this._saveTimer = setTimeout(() => this._save(), SAVE_DELAY_MS)
    }

    paste(event) {
        // Paste as text: a web page's markup (fonts, colours, tracking pixels)
        // must not ride along into an email, and the server would strip it anyway.
        const text = event.clipboardData?.getData("text/plain")
        if (text == null) return
        event.preventDefault()
        document.execCommand("insertText", false, text)
    }

    bodyKeydown(event) {
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault()
            this.deliver()
        }
    }

    toggleSignature() {
        const on = this.signatureTarget.dataset.on !== "true"
        this.signatureTarget.dataset.on = String(on)
        this.signatureTextTarget.hidden = !on
        this.signatureToggleTarget.setAttribute("aria-pressed", String(on))
        this.signatureLabelTarget.textContent = on ? "Signature added" : "Add signature"
        this.changed()
    }

    changeMailbox() {
        this.changed()
    }

    showCcBcc() {
        this.ccRowTarget.hidden = false
        this.bccRowTarget.hidden = false
        this.ccToggleTarget.hidden = true
        this._input("cc")?.focus()
    }

    // --- recipients ---------------------------------------------------------

    focusChipInput(event) {
        if (event.target.closest(".outgoing-chip-remove")) return
        const missing = event.target.closest(".outgoing-chip--missing")
        const field = event.currentTarget.dataset.field
        if (missing) {
            // A name Jane couldn't find an address for: turn it back into a
            // search so the user can pick the right person.
            const name = missing.dataset.name || ""
            missing.remove()
            const input = this._input(field)
            input.value = name
            input.focus()
            input.select()
            this._search(field, name)
            this.changed()
            return
        }
        if (event.target === event.currentTarget) this._input(field)?.focus()
    }

    searchRecipients(event) {
        const field = event.target.dataset.field
        const query = event.target.value
        // A comma or semicolon commits what was typed, as in a mail client.
        if (/[,;]\s*$/.test(query)) {
            event.target.value = query.replace(/[,;]\s*$/, "")
            this._commitTyped(field)
            return
        }
        clearTimeout(this._searchTimer)
        this._searchTimer = setTimeout(() => this._search(field, query), SEARCH_DELAY_MS)
    }

    chipKeydown(event) {
        const field = event.target.dataset.field
        const input = event.target
        const open = this.hasSuggestionsTarget && !this.suggestionsTarget.hidden && this._suggestions.length > 0
        if (event.key === "ArrowDown" && open) {
            event.preventDefault()
            this._highlight(Math.min(this._active + 1, this._suggestions.length - 1))
        } else if (event.key === "ArrowUp" && open) {
            event.preventDefault()
            this._highlight(Math.max(this._active - 1, 0))
        } else if (event.key === "Enter" || (event.key === "Tab" && input.value.trim())) {
            if (open && this._active >= 0) {
                event.preventDefault()
                this._pick(field, this._suggestions[this._active])
            } else if (input.value.trim()) {
                event.preventDefault()
                this._commitTyped(field)
            }
        } else if (event.key === "Escape") {
            this._hideSuggestions()
        } else if (event.key === "Backspace" && !input.value) {
            const chips = this._chips(field)
            const last = chips[chips.length - 1]
            if (last) {
                last.remove()
                this.changed()
            }
        }
    }

    chipFocus(event) {
        this._activeField = event.target.dataset.field
    }

    chipBlur(event) {
        const field = event.target.dataset.field
        // Late enough for a click on a suggestion to land first.
        setTimeout(() => {
            if (this.element.contains(document.activeElement) && document.activeElement === this._input(field)) return
            this._commitTyped(field, { onlyValid: true })
            this._hideSuggestions()
        }, 180)
    }

    removeChip(event) {
        event.preventDefault()
        event.stopPropagation()
        const chip = event.target.closest(".outgoing-chip")
        const field = chip?.closest(".outgoing-chips")?.dataset.field
        chip?.remove()
        this.changed()
        if (field) this._input(field)?.focus()
    }

    // --- sending ------------------------------------------------------------

    async deliver() {
        if (this._busy) return
        await this._flush()
        this._transition(this.deliverUrlValue)
    }

    async saveDraft() {
        this._closeMenu()
        if (this._busy) return
        await this._flush()
        this._transition(this.saveUrlValue, { pending: `Saving to ${this.providerValue} drafts…` })
    }

    undo() {
        this._transition(this.undoUrlValue)
    }

    async discard() {
        await this._flush()
        this._transition(this.discardUrlValue)
    }

    async copy() {
        this._closeMenu()
        const html = this._outgoingHtml()
        const text = this._outgoingText()
        try {
            if (window.ClipboardItem && navigator.clipboard?.write) {
                await navigator.clipboard.write([new ClipboardItem({
                    "text/html": new Blob([html], { type: "text/html" }),
                    "text/plain": new Blob([text], { type: "text/plain" })
                })])
            } else {
                await navigator.clipboard.writeText(text)
            }
            this._note("Copied")
        } catch (_e) {
            // The async clipboard is refused in some embeds and older browsers;
            // a selection plus execCommand still copies the formatted text.
            if (this._copyBySelection(html)) this._note("Copied")
            else this._error("Couldn't copy - select the text and copy it instead.")
        }
    }

    _copyBySelection(html) {
        const holder = document.createElement("div")
        holder.setAttribute("contenteditable", "true")
        holder.style.cssText = "position:fixed;left:-9999px;top:0;opacity:0;"
        holder.innerHTML = html
        document.body.append(holder)
        const selection = window.getSelection()
        const saved = selection.rangeCount > 0 ? selection.getRangeAt(0) : null
        const range = document.createRange()
        range.selectNodeContents(holder)
        selection.removeAllRanges()
        selection.addRange(range)
        let ok = false
        try { ok = document.execCommand("copy") } catch (_e) { ok = false }
        selection.removeAllRanges()
        if (saved) selection.addRange(saved)
        holder.remove()
        return ok
    }

    toggleMenu(event) {
        event.stopPropagation()
        const open = this.menuTarget.hidden
        this.menuTarget.hidden = !open
        this.caretTarget.setAttribute("aria-expanded", String(open))
        if (open) this.menuTarget.querySelector("button")?.focus()
    }

    // --- internals ----------------------------------------------------------

    _markDirty() {
        this._dirty = true
        this.element.dataset.liveCardDirty = "true"
    }

    _collect() {
        const body = {
            lock_version: this.lockVersionValue,
            to: this._recipients("to"),
            cc: this._recipients("cc"),
            bcc: this._recipients("bcc")
        }
        if (this.hasSubjectTarget) body.subject = this.subjectTarget.value
        if (this.hasBodyTarget) body.body_html = this.bodyTarget.innerHTML
        if (this.hasSignatureTarget) body.include_signature = this.signatureTarget.dataset.on === "true"
        if (this.hasMailboxTarget) body.mailbox_connection_id = this.mailboxTarget.value
        if (this._chatId) body.channel_chat_id = this._chatId
        return body
    }

    _recipients(field) {
        return this._chips(field).map((chip) => ({
            name: chip.dataset.name || "",
            email: chip.dataset.email || "",
            contact_id: chip.dataset.contactId || ""
        }))
    }

    _chips(field) {
        const list = this._chipList(field)
        return list ? Array.from(list.querySelectorAll(".outgoing-chip")) : []
    }

    _chipList(field) {
        const name = `${field}ChipsTarget`
        return this[`has${field[0].toUpperCase()}${field.slice(1)}ChipsTarget`] ? this[name] : null
    }

    _input(field) {
        return this._chipList(field)?.querySelector(".outgoing-chip-input")
    }

    // Saves now if anything is pending, and waits for it - Send must never race
    // the last keystrokes.
    async _flush() {
        clearTimeout(this._saveTimer)
        if (this._dirty) await this._save()
        else if (this._inflight) await this._inflight
    }

    _save({ keepalive = false } = {}) {
        if (!this.editable) return Promise.resolve()
        if (this._inflight) {
            this._again = true
            return this._inflight
        }
        this._dirty = false
        const payload = this._collect()
        this._inflight = this._request(this.updateUrlValue, "PATCH", payload, { keepalive })
            .then((data) => {
                if (data.lock_version != null) {
                    this.lockVersionValue = data.lock_version
                    this.element.dataset.liveCardVersion = String(data.lock_version)
                }
                if (!this._dirty) delete this.element.dataset.liveCardDirty
                this._error("")
            })
            .catch((err) => {
                this._dirty = true
                this._error(err.message)
            })
            .finally(() => {
                this._inflight = null
                if (this._again) {
                    this._again = false
                    this._save()
                }
            })
        return this._inflight
    }

    get editable() {
        return this.statusValue === "drafting" || this.statusValue === "failed"
    }

    async _transition(url, { pending, payload = {} } = {}) {
        if (this._busy) return
        this._busy = true
        this._setDisabled(true)
        if (pending) this._note(pending)
        try {
            const data = await this._request(url, "POST", payload)
            this._swap(data.html)
        } catch (err) {
            this._busy = false
            this._setDisabled(false)
            this._note("")
            // A refusal can carry the card as the server now has it (already
            // sent from another tab); show that, with the reason on it.
            const card = err.html ? this._swap(err.html) : this.element
            const error = card?.querySelector("[data-outgoing-draft-target='error']")
            if (error) {
                error.textContent = err.message
                error.hidden = false
            }
        }
    }

    _swap(html) {
        if (!html) return null
        const template = document.createElement("template")
        template.innerHTML = html.trim()
        const next = template.content.firstElementChild
        if (next) this.element.replaceWith(next)
        return next
    }

    async _request(url, method, payload, { keepalive = false } = {}) {
        const response = await fetch(url, {
            method,
            keepalive,
            credentials: "same-origin",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
            },
            body: method === "GET" ? undefined : JSON.stringify(payload)
        })
        const data = await response.json().catch(() => ({}))
        if (!response.ok || data.ok === false) {
            const error = new Error(data.error || "Couldn't save that - check your connection and try again.")
            error.html = data.html
            throw error
        }
        return data
    }

    _startCountdown() {
        clearInterval(this._countdownTimer)
        const sendAt = Date.parse(this.sendAtValue)
        const tick = () => {
            const left = Math.ceil((sendAt - Date.now()) / 1000)
            if (this.hasCountdownTarget) this.countdownTarget.textContent = left > 0 ? `Sending in ${left}s` : "Sending…"
            if (!(left > 0)) {
                clearInterval(this._countdownTimer)
                this._watch(Date.now())
            }
        }
        if (this.statusValue === "sending" && !Number.isNaN(sendAt)) {
            tick()
            this._countdownTimer = setInterval(tick, 250)
        } else {
            this._watch(Date.now())
        }
    }

    // After the window: ask until the draft reports sent or failed, then show it.
    _watch(startedAt) {
        clearTimeout(this._watchTimer)
        if (Date.now() - startedAt > WATCH_LIMIT_MS) return
        this._watchTimer = setTimeout(async () => {
            try {
                const response = await fetch(this.updateUrlValue, { headers: { Accept: "application/json" }, credentials: "same-origin" })
                const data = await response.json()
                if (data.status && data.status !== "sending" && data.status !== "delivering") {
                    this._swap(data.html)
                    return
                }
            } catch (_e) { /* keep watching */ }
            this._watch(startedAt)
        }, WATCH_INTERVAL_MS)
    }

    async _search(field, query) {
        const q = query.trim()
        if (q.length < 2) {
            this._hideSuggestions()
            return
        }
        this._searchField = field
        try {
            const response = await fetch(`${this.recipientsUrlValue}?q=${encodeURIComponent(q)}`, { headers: { Accept: "application/json" }, credentials: "same-origin" })
            const data = await response.json()
            if (this._input(field)?.value.trim() !== q) return
            const taken = new Set(["to", "cc", "bcc"].flatMap((f) => this._recipients(f).map((r) => r.email)))
            this._suggestions = (data.recipients || []).filter((r) => !taken.has(r.email))
            this._renderSuggestions(field)
        } catch (_e) {
            this._hideSuggestions()
        }
    }

    _renderSuggestions(field) {
        const box = this.suggestionsTarget
        box.replaceChildren()
        if (this._suggestions.length === 0) {
            this._hideSuggestions()
            return
        }
        this._suggestions.forEach((recipient, index) => {
            const row = document.createElement("button")
            row.type = "button"
            row.className = "outgoing-suggestion"
            row.setAttribute("role", "option")
            row.addEventListener("mousedown", (event) => {
                event.preventDefault()
                this._pick(field, recipient)
            })
            row.addEventListener("mouseenter", () => this._highlight(index))
            row.append(this._face(recipient))
            const text = document.createElement("span")
            text.className = "outgoing-suggestion-text"
            const name = document.createElement("span")
            name.className = "outgoing-suggestion-name"
            name.textContent = recipient.name || recipient.email
            const meta = document.createElement("span")
            meta.className = "outgoing-suggestion-meta"
            meta.textContent = [recipient.name ? recipient.email : null, recipient.meta].filter((part) => part && part !== recipient.name).join(" · ")
            text.append(name, meta)
            row.append(text)
            box.append(row)
        })
        const row = this[`${field}RowTarget`]
        box.style.top = `${row.offsetTop + row.offsetHeight}px`
        box.hidden = false
        this._highlight(0)
    }

    _face(recipient) {
        const face = document.createElement("span")
        face.className = "outgoing-chip-face"
        const initial = document.createElement("span")
        initial.className = "outgoing-chip-initial"
        initial.textContent = (recipient.name || recipient.email || "?").trim().charAt(0).toUpperCase()
        face.append(initial)
        if (recipient.avatar_url) {
            const img = document.createElement("img")
            img.src = recipient.avatar_url
            img.alt = ""
            face.append(img)
            this._dropIfBroken(img)
        }
        return face
    }

    // A photo that fails (an expired signed avatar link) is removed so the
    // initial underneath shows instead of a broken-image glyph. The error
    // event doesn't bubble, and a cached failure fires before connect.
    _dropIfBroken(img) {
        if (img.complete && img.naturalWidth === 0 && img.src) img.remove()
        else img.addEventListener("error", () => img.remove(), { once: true })
    }

    _highlight(index) {
        this._active = index
        Array.from(this.suggestionsTarget.children).forEach((row, i) => row.classList.toggle("is-active", i === index))
    }

    _hideSuggestions() {
        if (!this.hasSuggestionsTarget) return
        this.suggestionsTarget.hidden = true
        this.suggestionsTarget.replaceChildren()
        this._suggestions = []
        this._active = -1
    }

    _pick(field, recipient) {
        this._addChip(field, recipient)
        const input = this._input(field)
        if (input) {
            input.value = ""
            input.focus()
        }
        this._hideSuggestions()
    }

    _commitTyped(field, { onlyValid = false } = {}) {
        const input = this._input(field)
        if (!input) return
        const value = input.value.trim()
        if (!value) return
        const match = value.match(/^(.*)<([^>]+)>$/)
        const email = (match ? match[2] : value).trim().toLowerCase()
        if (EMAIL.test(email)) {
            this._addChip(field, { name: match ? match[1].trim().replace(/^"|"$/g, "") : "", email })
            input.value = ""
            this._hideSuggestions()
        } else if (!onlyValid) {
            this._error(`"${value}" isn't an email address - pick someone from the list or type their address.`)
        }
    }

    _addChip(field, recipient) {
        const list = this._chipList(field)
        if (!list || !this.hasChipTemplateTarget) return
        const chip = this.chipTemplateTarget.content.firstElementChild.cloneNode(true)
        const name = recipient.name || recipient.email
        chip.dataset.name = recipient.name || ""
        chip.dataset.email = recipient.email || ""
        chip.dataset.contactId = recipient.contact_id || ""
        chip.title = recipient.email || ""
        chip.querySelector(".outgoing-chip-name").textContent = name
        chip.querySelector(".outgoing-chip-remove")?.setAttribute("aria-label", `Remove ${name}`)
        chip.querySelector(".outgoing-chip-face").replaceWith(this._face(recipient))
        list.insertBefore(chip, this._input(field))
        const input = this._input(field)
        if (input) input.placeholder = ""
        this._error("")
        this.changed()
    }

    _outgoingHtml() {
        const body = this.hasBodyTarget ? this.bodyTarget.innerHTML : ""
        const signatureOn = this.hasSignatureTarget && this.signatureTarget.dataset.on === "true"
        if (!signatureOn) return body
        const lines = this.signatureTextTarget.textContent.trim().split(/\r?\n/).map((line) => {
            const span = document.createElement("span")
            span.textContent = line
            return span.innerHTML
        })
        return `${body}<p>${lines.join("<br>")}</p>`
    }

    _outgoingText() {
        const body = this.hasBodyTarget ? this.bodyTarget.innerText.trim() : ""
        const signatureOn = this.hasSignatureTarget && this.signatureTarget.dataset.on === "true"
        return signatureOn ? `${body}\n\n${this.signatureTextTarget.textContent.trim()}` : body
    }

    _setDisabled(disabled) {
        this.element.querySelectorAll(".outgoing-footer button").forEach((button) => { button.disabled = disabled })
    }

    async _loadChats(query) {
        try {
            const response = await fetch(`${this.chatsUrlValue}?q=${encodeURIComponent(query.trim())}`, { headers: { Accept: "application/json" }, credentials: "same-origin" })
            const data = await response.json()
            this._renderChats(data.chats || [])
        } catch (_e) {
            this._renderChats([])
        }
    }

    _renderChats(chats) {
        const list = this.chatListTarget
        list.replaceChildren()
        if (chats.length === 0) {
            const empty = document.createElement("p")
            empty.className = "outgoing-chat-empty"
            empty.textContent = "No conversations match."
            list.append(empty)
            return
        }
        chats.forEach((chat, index) => {
            const row = document.createElement("button")
            row.type = "button"
            row.className = "outgoing-chat-row"
            row.setAttribute("role", "option")
            if (index === 0) row.classList.add("is-active")
            row.append(this._chatBadge(chat))
            const name = document.createElement("span")
            name.className = "outgoing-chat-row-name"
            name.textContent = chat.name
            row.append(name)
            row.addEventListener("click", () => this._pickChat(chat))
            list.append(row)
        })
    }

    _chatBadge(chat) {
        if (chat.group) {
            const count = document.createElement("span")
            count.className = "outgoing-chat-count"
            count.textContent = String(chat.people)
            return count
        }
        return this._face({ name: chat.name, avatar_url: chat.avatar_url })
    }

    _pickChat(chat) {
        this._chatId = chat.id
        const button = this.chatButtonTarget
        const chevron = button.querySelector(".outgoing-chat-chevron")
        const name = document.createElement("span")
        name.className = "outgoing-chat-name"
        name.textContent = chat.name
        button.replaceChildren(this._chatBadge(chat), name)
        if (chevron) button.append(chevron)
        button.classList.remove("outgoing-chat-button--missing")
        this._closeChats()
        this._error("")
        this._markDirty()
        this._save()
    }

    _closeChats() {
        if (!this.hasChatMenuTarget) return
        this.chatMenuTarget.hidden = true
        this.chatButtonTarget.setAttribute("aria-expanded", "false")
    }

    _restoreBodyRange() {
        if (!this.hasBodyTarget) return
        this.bodyTarget.focus({ preventScroll: true })
        if (!this._bodyRange) return
        const selection = window.getSelection()
        selection.removeAllRanges()
        selection.addRange(this._bodyRange)
    }

    _closest(tag) {
        const selection = window.getSelection()
        let node = selection?.anchorNode
        while (node && node !== this.bodyTarget) {
            if (node.nodeName?.toLowerCase() === tag) return node
            node = node.parentNode
        }
        return null
    }

    // Toggle an inline element (inline code) around the selection.
    _wrapInline(tag) {
        const existing = this._closest(tag)
        if (existing) {
            existing.replaceWith(...existing.childNodes)
            return
        }
        const selection = window.getSelection()
        if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return
        const range = selection.getRangeAt(0)
        const element = document.createElement(tag)
        element.append(range.extractContents())
        range.insertNode(element)
        selection.removeAllRanges()
        const after = document.createRange()
        after.selectNodeContents(element)
        selection.addRange(after)
    }

    _closeMenu() {
        if (!this.hasMenuTarget) return
        this.menuTarget.hidden = true
        this.caretTarget.setAttribute("aria-expanded", "false")
    }

    _note(message) {
        if (!this.hasNoteTarget) return
        this.noteTarget.textContent = message
        clearTimeout(this._noteTimer)
        if (message === "Copied") this._noteTimer = setTimeout(() => { this.noteTarget.textContent = "" }, 1800)
    }

    _error(message) {
        if (!this.hasErrorTarget) return
        this.errorTarget.textContent = message
        this.errorTarget.hidden = !message
    }
}
